import { pluralRu } from './format';
import { countryConfig } from '../country';

export type NicheHint = { tier: 'niche' | 'direction' | 'daily'; count: number };

type T = (key: string, params?: Record<string, string | number>) => string;

/** MiLicita: Spanish is the deployment's only locale and no other deployment
 *  serves it — the same deployment read `pluralKeyIndex` (format.ts) makes, so
 *  none of the three callers has to thread a locale through. */
const SPANISH = countryConfig.locales.includes('es');

/** Russian's 1 / 2-4 / 5+ forms, or Spanish's 1 / other on the ES build. */
function agree(n: number, ru: [string, string, string], es: [string, string]): string {
  if (SPANISH) return n === 1 ? es[0] : es[1];
  return pluralRu(n, ...ru);
}

/**
 * The hint worth printing, or `undefined`. A count of 0 («Para tus búsquedas se
 * publican 0 lotes nuevos al mes», phone QA 26.10) turns the best argument under
 * the price into a reason not to pay, so no line at all is said instead — on
 * every deployment. A positive count passes through untouched.
 */
export function shownNicheHint(hint: NicheHint | null | undefined): NicheHint | undefined {
  return hint && hint.count >= 1 ? hint : undefined;
}

/**
 * The value-line under the price, pluralised.
 *
 * Russian agrees the noun (and the verb «появляется/появляются») with the
 * count: «2 новых лота», «5 новых лотов», «1 новый лот». Baking a fixed noun
 * into the i18n string printed the 5+ form for every count — «4 новых лотов».
 * The `{{lots}}`/`{{appears}}` placeholders are filled here via `pluralRu`; the
 * Uzbek templates ignore them (Uzbek is invariant — «lot» never changes).
 * Spanish has one form for 1 and one for the rest: «se publica 1 lote nuevo»,
 * «se publican 5 lotes nuevos».
 */
export function nicheHintLine(hint: NicheHint, t: T): string {
  const n = hint.count;
  if (hint.tier === 'niche') {
    return t('billing.card.nicheOpen', {
      count: n,
      lots: agree(
        n,
        ['открытый лот', 'открытых лота', 'открытых лотов'],
        ['lote abierto', 'lotes abiertos'],
      ),
    });
  }
  const appears = agree(
    n,
    ['появляется', 'появляются', 'появляется'],
    ['se publica', 'se publican'],
  );
  const lots = agree(n, ['новый лот', 'новых лота', 'новых лотов'], ['lote nuevo', 'lotes nuevos']);
  return hint.tier === 'direction'
    ? t('billing.card.nicheDirection', { count: n, lots, appears })
    : t('billing.card.nicheDaily', { count: n, lots, appears });
}
